import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TemplateBuilder = ({ template, onSave, onCancel }) => {
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    departmentId: '',
    isDefault: false,
    tasks: []
  });

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigneeType: 'employee',
    dueOffsetDays: 1
  });

  useEffect(() => {
    if (template) {
      setTemplateData({
        name: template?.name || '',
        description: template?.description || '',
        departmentId: template?.departmentId || '',
        isDefault: template?.isDefault || false,
        tasks: template?.tasks || []
      });
    }
  }, [template]);

  const assigneeTypeOptions = [
    { value: 'employee', label: 'Employee' },
    { value: 'manager', label: 'Manager' },
    { value: 'it', label: 'IT Team' },
    { value: 'other', label: 'Other' }
  ];

  const departmentOptions = [
    { value: '', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Sales' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' }
  ];

  const handleAddTask = () => {
    if (!newTask?.title?.trim()) return;

    const task = {
      id: Date.now()?.toString(),
      title: newTask?.title,
      description: newTask?.description,
      assigneeType: newTask?.assigneeType,
      dueOffsetDays: parseInt(newTask?.dueOffsetDays) || 1,
      isCompleted: false
    };

    setTemplateData(prev => ({
      ...prev,
      tasks: [...prev?.tasks, task]
    }));

    setNewTask({
      title: '',
      description: '',
      assigneeType: 'employee',
      dueOffsetDays: 1
    });
  };

  const handleRemoveTask = (taskId) => {
    setTemplateData(prev => ({
      ...prev,
      tasks: prev?.tasks?.filter(task => task?.id !== taskId)
    }));
  };

  const handleUpdateTask = (taskId, field, value) => {
    setTemplateData(prev => ({
      ...prev,
      tasks: prev?.tasks?.map(task =>
        task?.id === taskId ? { ...task, [field]: value } : task
      )
    }));
  };

  const handleSave = () => {
    if (!templateData?.name?.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (templateData?.tasks?.length === 0) {
      alert('Please add at least one task');
      return;
    }

    onSave(templateData);
  };

  const getAssigneeTypeColor = (type) => {
    switch (type) {
      case 'employee':
        return 'bg-blue-100 text-blue-800';
      case 'manager':
        return 'bg-green-100 text-green-800';
      case 'it':
        return 'bg-purple-100 text-purple-800';
      case 'other':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? 'Edit Template' : 'Create New Template'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Template Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <Input
                  value={templateData?.name}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="e.g., Software Engineer Onboarding"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                <Select
                  value={templateData?.departmentId}
                  onChange={(value) => setTemplateData(prev => ({ ...prev, departmentId: value }))}
                  options={departmentOptions}
                  placeholder="Select department"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={templateData?.description}
                onChange={(e) => setTemplateData(prev => ({ ...prev, description: e?.target?.value }))}
                placeholder="Describe this onboarding template..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isDefault"
                checked={templateData?.isDefault}
                onChange={(e) => setTemplateData(prev => ({ ...prev, isDefault: e?.target?.checked }))}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                Set as default template for new hires
              </label>
            </div>

            {/* Add New Task */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Task</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Title *
                  </label>
                  <Input
                    value={newTask?.title}
                    onChange={(e) => setNewTask(prev => ({ ...prev, title: e?.target?.value }))}
                    placeholder="e.g., Complete IT setup"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned To
                  </label>
                  <Select
                    value={newTask?.assigneeType}
                    onChange={(value) => setNewTask(prev => ({ ...prev, assigneeType: value }))}
                    options={assigneeTypeOptions}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <Input
                    value={newTask?.description}
                    onChange={(e) => setNewTask(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Optional task description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Days (after start)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={newTask?.dueOffsetDays}
                    onChange={(e) => setNewTask(prev => ({ ...prev, dueOffsetDays: e?.target?.value }))}
                  />
                </div>
              </div>
              <Button onClick={handleAddTask} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </div>

            {/* Task List */}
            {templateData?.tasks?.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Tasks ({templateData?.tasks?.length})
                </h3>
                <div className="space-y-3">
                  {templateData?.tasks?.map((task, index) => (
                    <div key={task?.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-500 mr-3">
                            #{index + 1}
                          </span>
                          <div>
                            <h4 className="font-medium text-gray-900">{task?.title}</h4>
                            {task?.description && (
                              <p className="text-sm text-gray-600 mt-1">{task?.description}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveTask(task?.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAssigneeTypeColor(task?.assigneeType)}`}>
                          {task?.assigneeType}
                        </span>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          Due in {task?.dueOffsetDays} day{task?.dueOffsetDays !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;